<template>
  <div class="setting-gallery-step">
    <!-- 风格选择区 -->
    <div class="section style-section">
      <StyleSelector v-model="selectedStyle" @change="onStyleChange" />
    </div>

    <!-- 设定列表区 -->
    <div class="section settings-section">
      <div class="section-header">
        <h3>设定图生成</h3>
        <p class="section-desc">为每个设定生成设定图，支持生成1-3张供选择</p>
      </div>

      <!-- 角色设定 -->
      <div v-if="groupedSettings.character?.length" class="setting-group">
        <h4 class="group-title">
          <el-icon><User /></el-icon>
          角色设定
        </h4>
        <div class="setting-list">
          <SettingGalleryItem
            v-for="setting in groupedSettings.character"
            :key="setting.id"
            :setting="setting"
            :setting-type="'character'"
            :selected-style="selectedStyle"
            :images="settingImages[setting.id] || []"
            @update-field="updateSettingField"
            @generate="generateImages"
            @set-default="setDefaultImage"
            @preview="previewImage"
            @delete="deleteImage"
          />
        </div>
        <div class="collage-action">
          <el-button
            size="small"
            type="warning"
            :loading="generatingCollage === 'character'"
            @click="generateCollage('character')"
          >
            合并角色设定图
          </el-button>
          <span v-if="collages.character" class="collage-status success">已合并</span>
        </div>
      </div>

      <!-- 地点设定 -->
      <div v-if="groupedSettings.location?.length" class="setting-group">
        <h4 class="group-title">
          <el-icon><Location /></el-icon>
          地点设定
        </h4>
        <div class="setting-list">
          <SettingGalleryItem
            v-for="setting in groupedSettings.location"
            :key="setting.id"
            :setting="setting"
            :setting-type="'location'"
            :selected-style="selectedStyle"
            :images="settingImages[setting.id] || []"
            @update-field="updateSettingField"
            @generate="generateImages"
            @set-default="setDefaultImage"
            @preview="previewImage"
            @delete="deleteImage"
          />
        </div>
        <div class="collage-action">
          <el-button
            size="small"
            type="warning"
            :loading="generatingCollage === 'location'"
            @click="generateCollage('location')"
          >
            合并地点设定图
          </el-button>
          <span v-if="collages.location" class="collage-status success">已合并</span>
        </div>
      </div>

      <!-- 道具设定 -->
      <div v-if="groupedSettings.prop?.length" class="setting-group">
        <h4 class="group-title">
          <el-icon><Box /></el-icon>
          道具设定
        </h4>
        <div class="setting-list">
          <SettingGalleryItem
            v-for="setting in groupedSettings.prop"
            :key="setting.id"
            :setting="setting"
            :setting-type="'prop'"
            :selected-style="selectedStyle"
            :images="settingImages[setting.id] || []"
            @update-field="updateSettingField"
            @generate="generateImages"
            @set-default="setDefaultImage"
            @preview="previewImage"
            @delete="deleteImage"
          />
        </div>
        <div class="collage-action">
          <el-button
            size="small"
            type="warning"
            :loading="generatingCollage === 'prop'"
            @click="generateCollage('prop')"
          >
            合并道具设定图
          </el-button>
          <span v-if="collages.prop" class="collage-status success">已合并</span>
        </div>
      </div>

      <!-- 世界观设定 -->
      <div v-if="groupedSettings.worldview?.length" class="setting-group">
        <h4 class="group-title">
          <el-icon><Collection /></el-icon>
          世界观设定
        </h4>
        <div class="setting-list">
          <SettingGalleryItem
            v-for="setting in groupedSettings.worldview"
            :key="setting.id"
            :setting="setting"
            :setting-type="'worldview'"
            :selected-style="selectedStyle"
            :images="settingImages[setting.id] || []"
            @update-field="updateSettingField"
            @generate="generateImages"
            @set-default="setDefaultImage"
            @preview="previewImage"
            @delete="deleteImage"
          />
        </div>
        <div class="collage-action">
          <el-button
            size="small"
            type="warning"
            :loading="generatingCollage === 'worldview'"
            @click="generateCollage('worldview')"
          >
            合并世界观设定图
          </el-button>
          <span v-if="collages.worldview" class="collage-status success">已合并</span>
        </div>
      </div>

      <!-- 一键合并全部 -->
      <div v-if="hasSettings" class="collage-all-section">
        <el-button
          type="primary"
          :loading="generatingCollage === 'all'"
          @click="generateAllCollages"
        >
          一键合并全部设定图
        </el-button>
        <p class="help-text">合并设定图后，在生成分镜时将作为参考图传递给AI，确保角色、地点、道具风格一致</p>
      </div>

      <!-- 空状态 -->
      <el-empty
        v-if="!hasSettings"
        description="暂无设定数据，请先在设定管理中提取设定"
      >
        <el-button type="primary" @click="goToSettings">前往设定管理</el-button>
      </el-empty>
    </div>

    <!-- 底部操作 -->
    <div class="section actions-section">
      <el-button @click="goToPrevStep">返回设定管理</el-button>
      <el-button
        type="primary"
        :loading="saving"
        :disabled="!canProceed"
        @click="saveAndProceed"
      >
        保存并继续
        <el-icon class="el-icon--right"><ArrowRight /></el-icon>
      </el-button>
    </div>

    <!-- 图片预览弹窗 -->
    <ImagePreviewModal
      v-model="previewModalVisible"
      :images="previewImages"
      :current-index="previewCurrentIndex"
      :setting-name="previewSettingName"
      @set-default="setDefaultImage"
      @download="downloadImage"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { User, Location, Box, Collection, ArrowRight } from '@element-plus/icons-vue'
import { useProjectStore } from '../../stores/project'
import StyleSelector from './StyleSelector.vue'
import SettingGalleryItem from './SettingGalleryItem.vue'
import ImagePreviewModal from './ImagePreviewModal.vue'
import api from '../../api/index.js'

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['step-complete', 'step-save'])

const router = useRouter()
const projectStore = useProjectStore()

// 状态
const selectedStyle = ref('')
const settings = ref([])
const settingImages = ref({})
const saving = ref(false)
const loading = ref(false)
const generatingCollage = ref(null)
const collages = ref({})

// 预览弹窗状态
const previewModalVisible = ref(false)
const previewImages = ref([])
const previewCurrentIndex = ref(0)
const previewSettingName = ref('')

// 计算属性
const groupedSettings = computed(() => {
  const grouped = {
    character: [],
    location: [],
    prop: [],
    worldview: []
  }

  settings.value.forEach(setting => {
    const type = setting.setting_type
    if (grouped[type]) {
      grouped[type].push(setting)
    }
  })

  return grouped
})

const hasSettings = computed(() => settings.value.length > 0)

const canProceed = computed(() => {
  // 检查是否所有设定都有默认图
  if (!hasSettings.value) return false

  for (const setting of settings.value) {
    const images = settingImages.value[setting.id] || []
    const hasDefault = images.some(img => img.is_default)
    if (!hasDefault) return false
  }

  return true
})

// 方法
const fetchSettings = async () => {
  loading.value = true
  try {
    const result = await api.get(`/projects/${props.projectId}/settings`)
    if (result.success) {
      settings.value = result.data || []
      for (const setting of settings.value) {
        await fetchSettingImages(setting.id)
      }
    }
  } catch (error) {
    ElMessage.error(error.message || '获取设定列表失败')
  } finally {
    loading.value = false
  }
}

const fetchSettingImages = async (settingId) => {
  try {
    const result = await api.get(`/settings/${settingId}/images`)
    if (result.success) {
      settingImages.value[settingId] = result.data || []
    }
  } catch (error) {
    console.error('获取设定图失败:', error)
  }
}

const onStyleChange = (styleKey) => {
  console.log('选择风格:', styleKey)
}

const updateSettingField = async (settingId, fields) => {
  try {
    const result = await api.put(`/settings/${settingId}/fields`, fields)
    if (result.success) {
      const setting = settings.value.find(s => s.id === settingId)
      if (setting) {
        setting.setting_value = JSON.stringify(result.data.setting_value)
      }
      ElMessage.success('已保存')
    }
  } catch (error) {
    ElMessage.error(error.message || '更新设定字段失败')
  }
}

const generateImages = async (settingId, count) => {
  if (!selectedStyle.value) {
    ElMessage.warning('请先选择视觉风格')
    return
  }

  try {
    ElMessage.info('正在生成设定图，请稍候...')

    const result = await api.post(`/settings/${settingId}/images/generate`, {
      style_key: selectedStyle.value,
      count: count,
      editable_fields: {}
    })

    if (result.success) {
      ElMessage.success('设定图生成任务已创建')
      await fetchSettingImages(settingId)
    } else {
      throw new Error(result.message || '生成失败')
    }
  } catch (error) {
    ElMessage.error(error.message || '生成设定图失败')
  }
}

const setDefaultImage = async (imageId, settingId) => {
  try {
    const result = await api.put(`/setting-images/${imageId}/default`)
    if (result.success) {
      const images = settingImages.value[settingId] || []
      images.forEach(img => {
        img.is_default = (img.id === imageId)
      })
      ElMessage.success('已设为默认设定图')
    }
  } catch (error) {
    ElMessage.error(error.message || '设置默认图失败')
  }
}

const previewImage = (images, currentIndex, settingName) => {
  previewImages.value = images
  previewCurrentIndex.value = currentIndex
  previewSettingName.value = settingName
  previewModalVisible.value = true
}

const deleteImage = async (imageId, settingId) => {
  try {
    const result = await api.delete(`/setting-images/${imageId}`)
    if (result.success) {
      ElMessage.success('图片已删除')
      await fetchSettingImages(settingId)
    } else {
      throw new Error(result.message || '删除失败')
    }
  } catch (error) {
    ElMessage.error(error.message || '删除图片失败')
  }
}

const downloadImage = (imageUrl) => {
  const link = document.createElement('a')
  link.href = imageUrl
  link.download = `setting-image-${Date.now()}.png`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const generateCollage = async (type) => {
  generatingCollage.value = type
  try {
    const data = await api.post(`/projects/${props.projectId}/setting-collages/generate`, {
      type: type
    })
    if (data.success) {
      const collageData = data.data.collages || {}
      if (collageData[type]) {
        collages.value[type] = collageData[type]
      }
      ElMessage.success(`${type === 'character' ? '角色' : type === 'location' ? '地点' : type === 'prop' ? '道具' : '世界观'}设定图合并成功`)
    } else {
      ElMessage.error(data.message || '合并失败')
    }
  } catch (error) {
    ElMessage.error('合并设定图失败: ' + error.message)
  } finally {
    generatingCollage.value = null
  }
}

const generateAllCollages = async () => {
  generatingCollage.value = 'all'
  try {
    const data = await api.post(`/projects/${props.projectId}/setting-collages/generate`)
    if (data.success) {
      const collageData = data.data.collages || {}
      Object.keys(collageData).forEach(type => {
        collages.value[type] = collageData[type]
      })
      const count = Object.keys(collageData).length
      ElMessage.success(`已成功合并 ${count} 类设定图`)
    } else {
      ElMessage.error(data.message || '合并失败')
    }
  } catch (error) {
    ElMessage.error('合并设定图失败: ' + error.message)
  } finally {
    generatingCollage.value = null
  }
}

const loadCollages = async () => {
  try {
    const data = await api.get(`/projects/${props.projectId}/setting-collages`)
    if (data.success && data.data) {
      collages.value = data.data
    }
  } catch (error) {
    // 静默失败，合并图可能还不存在
  }
}

const goToSettings = () => {
  emit('step-complete', 'settings')
}

const goToPrevStep = () => {
  emit('step-complete', 'settings')
}

const saveAndProceed = async () => {
  if (!canProceed.value) {
    ElMessage.warning('请为所有设定生成并选择默认设定图')
    return
  }

  saving.value = true
  try {
    // 更新项目状态
    await projectStore.updateProjectStatus('setting_images_generated')

    ElMessage.success('设定图集已保存')
    emit('step-complete')
  } catch (error) {
    ElMessage.error('保存失败: ' + error.message)
  } finally {
    saving.value = false
  }
}

// 初始化
onMounted(() => {
  fetchSettings()
  loadCollages()
})
</script>

<style scoped lang="scss">
.setting-gallery-step {
  .section {
    margin-bottom: 24px;
    padding: 20px;
    background: var(--el-bg-color);
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
    border: 1px solid var(--el-border-color-light);
  }

  .section-header {
    margin-bottom: 20px;

    h3 {
      font-size: 18px;
      font-weight: 600;
      color: var(--el-text-color-primary);
      margin: 0 0 8px;
    }

    .section-desc {
      font-size: 14px;
      color: var(--el-text-color-secondary);
      margin: 0;
    }
  }

  .setting-group {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    .group-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 16px;
      font-weight: 600;
      color: var(--el-text-color-primary);
      margin: 0 0 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid var(--el-border-color);

      .el-icon {
        color: var(--el-color-primary);
      }
    }

    .setting-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }

  .actions-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .collage-action {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed var(--el-border-color-lighter);

    .collage-status {
      font-size: 13px;

      &.success {
        color: var(--el-color-success);
      }
    }
  }

  .collage-all-section {
    margin-top: 16px;
    padding: 16px;
    background: var(--el-color-primary-light-9);
    border-radius: 8px;
    border: 1px solid var(--el-color-primary-light-7);
    text-align: center;

    .help-text {
      margin-top: 8px;
      font-size: 13px;
      color: var(--el-text-color-secondary);
    }
  }
}
</style>
